import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import JSZip from "jszip";
import { saveAs } from "file-saver";

/**
 * By Hudson Raone - Media Download Utility
 * Versão 8.0 - Share API nativa para Android + Suporte Avançado iOS/PWA
 * 
 * ANDROID: Usa navigator.share() com File para salvar diretamente na Galeria
 * sem popup de autenticação ou email
 * iOS: Abre nova aba para "Pressionar e segurar → Salvar em Fotos"
 * Desktop: Blob download com <a download>
 */

const isIOS = () =>
 /iPad|iPhone|iPod/.test(navigator.userAgent) ||
 (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const isAndroid = () => /Android/i.test(navigator.userAgent);

const isMobile = () => isIOS() || isAndroid();

const isInAppBrowser = () =>
 /Instagram|FBAN|FBAV|TwitterAndroid|Line\/|MicroMessenger/i.test(navigator.userAgent);

/**
 * Salva uma imagem diretamente na galeria do Android usando a Web Share API.
 * Esta API funciona no Chrome Android 75+ e salva sem popup de autenticação.
 */
const saveToGalleryAndroid = async (blob: Blob, filename: string): Promise<boolean> => {
 // Web Share API com File — salva direto na galeria
 if (navigator.canShare && navigator.share) {
 const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
 if (navigator.canShare({ files: [file] })) {
 try {
 await navigator.share({
 files: [file],
 title: 'By Hudson Raone',
 text: '📸 Sua foto do By Hudson Raone',
 });
 return true;
 } catch (err: any) {
 // Usuário cancelou o share — não é erro
 if (err.name === 'AbortError') return false;
 console.warn('Share API falhou, tentando fallback...', err);
 }
 }
 }
 return false;
};

/**
 * Força o download de um blob como arquivo via <a download>
 */
const blobDownload = (blob: Blob, filename: string) => {
 const blobUrl = URL.createObjectURL(blob);
 const link = document.createElement('a');
 link.href = blobUrl;
 link.download = filename;
 link.style.display = 'none';
 document.body.appendChild(link);
 link.click();
 setTimeout(() => {
 document.body.removeChild(link);
 URL.revokeObjectURL(blobUrl);
 }, 200);
};

/**
 * Busca um Blob de uma URL, tentando contornar CORS quando necessário.
 */
export const fetchBlob = async (url: string): Promise<Blob | null> => {
 try {
 const res = await fetch(url, { mode: 'cors', cache: 'no-store' });
 if (res.ok) return await res.blob();
 } catch (e) {
 console.warn('CORS fetch falhou, tentando no-cors...', e);
 }
 try {
 // no-cors retorna opaque blob que ainda pode ser usado com URL.createObjectURL
 const res = await fetch(url, { mode: 'no-cors', cache: 'no-store' });
 const blob = await res.blob();
 if (blob.size > 0) return blob;
 } catch (e) {
 console.warn('no-cors fetch também falhou:', e);
 }
 return null;
};

/**
 * Gera URL assinada do Supabase Storage (sem exigir autenticação do usuário)
 */
export const getSupabaseSignedUrl = async (url: string, forDownload: boolean): Promise<string | null> => {
 if (!url.includes('supabase.co/storage/v1/object/')) return null;

 const markers = ['/public/', '/sign/', '/authenticated/'];
 let matchedMarker = '';
 for (const m of markers) {
 if (url.includes(m)) { matchedMarker = m; break; }
 }
 if (!matchedMarker) return null;

 const parts = url.split(matchedMarker)[1].split('/');
 const bucket = parts[0];
 const path = parts.slice(1).join('/').split('?')[0];

 if (!bucket || !path) return null;

 try {
 // URL pública (sem autenticação) — funciona mesmo sem login
 if (!forDownload) {
 const { data } = supabase.storage.from(bucket).getPublicUrl(path);
 return data?.publicUrl?.split('?')[0] || null;
 }
 // URL assinada para download — válida por 1 hora
 const { data, error } = await supabase.storage
 .from(bucket)
 .createSignedUrl(path, 3600, { download: forDownload ? path.split('/').pop() || 'foto.jpg' : false });
 if (error) {
 // Fallback: URL pública sem header de download
 const { data: pub } = supabase.storage.from(bucket).getPublicUrl(path);
 return pub?.publicUrl || null;
 }
 return data?.signedUrl || null;
 } catch (e) {
 return null;
 }
};

/**
 * downloadMedia — Função principal de download
 * 
 * Android: Share API → salva direto na galeria SEM popup de autenticação
 * iOS: Abre nova aba → usuário pressiona e segura → Salvar em Fotos
 * Desktop: Blob download automático
 */
export const downloadMedia = async (url: string, filename?: string): Promise<boolean> => {
 if (!url) return false;

 const ios = isIOS();
 const android = isAndroid();
 const inApp = isInAppBrowser();
 const defaultFilename = url.split('/').pop()?.split('?')[0] || 'foto_by_hudson.jpg';
 const finalFilename = filename || defaultFilename;

 console.log(`🚀 Download v8.0 [${ios ? 'iOS' : android ? 'Android' : 'Desktop'} | inApp=${inApp}]:`, finalFilename);

 // ============================================================
 // 📱 ANDROID — Usar Share API para salvar direto na galeria
 // ============================================================
 if (android && !inApp) {
 try {
 // 1. Resolver URL final (Supabase → signed URL)
 let resolvedUrl = url;
 const signedUrl = await getSupabaseSignedUrl(url, false);
 if (signedUrl) resolvedUrl = signedUrl;

 // Para Google Drive, criar URL direta
 if (url.includes('drive.google.com')) {
 const idMatch = url.match(/[-\w]{25,}/);
 if (idMatch) resolvedUrl = `https://drive.google.com/uc?export=download&id=${idMatch[0]}`;
 }

 // 2. Buscar o blob da imagem
 toast.loading('Preparando download...', { id: 'dl-android' });
 const blob = await fetchBlob(resolvedUrl);

 if (blob && blob.size > 100) {
 toast.dismiss('dl-android');
 // 3. Share API — salva direto na galeria Android
 const shared = await saveToGalleryAndroid(blob, finalFilename);
 if (shared) {
 toast.success('Foto salva! Verifique sua galeria 📸', { duration: 4000 });
 return true;
 }
 // Se share não funcionou, usar <a download>
 blobDownload(blob, finalFilename);
 toast.success('Download iniciado!');
 return true;
 }
 } catch (err) {
 console.warn('Android download falhou, usando fallback...', err);
 toast.dismiss('dl-android');
 }

 // Fallback Android: link direto
 window.open(url, '_blank');
 toast.info('Abrindo foto para download...');
 return true;
 }

 // ============================================================
 // 🍎 iOS — Mostra a foto diretamente na nova aba
 // ============================================================
 if (ios) {
  let resolvedUrl = url;

  // Resolver URL antes de abrir aba (tentar signed URL)
  try {
   if (url.includes('drive.google.com')) {
    const idMatch = url.match(/[-\w]{25,}/);
    if (idMatch) resolvedUrl = `https://drive.google.com/uc?export=view&id=${idMatch[0]}`;
   } else {
    const pubUrl = await getSupabaseSignedUrl(url, false);
    if (pubUrl) resolvedUrl = pubUrl;
   }
  } catch (_) {
   resolvedUrl = url;
  }

  // Abrir nova aba com a imagem exibida diretamente (press-hold to save)
  const newTab = window.open('about:blank', '_blank');
  if (newTab) {
   newTab.document.write(`<!DOCTYPE html>
<html>
<head>
 <meta name="viewport" content="width=device-width, initial-scale=1.0">
 <meta charset="UTF-8">
 <title>Sua Foto</title>
 <style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
   background: #0F0F10; color: #FFF;
   font-family: -apple-system, sans-serif;
   display: flex; flex-direction: column;
   align-items: center; justify-content: flex-start;
   min-height: 100vh; padding: 24px 16px;
   text-align: center;
  }
  .logo { font-size: 13px; font-weight: 900; color: rgba(255,255,255,0.4);
   letter-spacing: 0.15em; margin-bottom: 16px; }
  .title { font-size: 20px; font-weight: 900; margin-bottom: 6px; }
  .hint { color: rgba(255,255,255,0.55); font-size: 14px; margin-bottom: 20px; line-height: 1.5; }
  .photo-wrap { width: 100%; max-width: 480px; border-radius: 16px; overflow: hidden;
   box-shadow: 0 8px 40px rgba(0,0,0,0.6); margin-bottom: 20px; }
  .photo-wrap img { width: 100%; height: auto; display: block; }
  .btn { background: #FF6A00; color: #FFF; border: none;
   padding: 16px 32px; border-radius: 14px;
   font-size: 16px; font-weight: 900; cursor: pointer;
   text-decoration: none; display: inline-block; margin-top: 8px; }
  .err { color: rgba(255,100,100,0.8); font-size: 13px; margin-top: 16px; display: none; }
 </style>
</head>
<body>
 <div class="logo">BY HUDSON RAONE</div>
 <div class="title">📸 Sua Foto Original</div>
 <div class="hint">
  Pressione e segure na foto abaixo<br>
  e toque em <strong>Salvar em Fotos</strong>
 </div>

 <div class="photo-wrap">
  <img
   id="photo"
   src="${resolvedUrl}"
   alt="Sua foto"
   onerror="document.getElementById('errMsg').style.display='block'; document.getElementById('fallbackBtn').style.display='inline-block';"
   crossorigin="anonymous"
  />
 </div>

 <a id="fallbackBtn" href="${resolvedUrl}" target="_blank" class="btn" style="display:none;">
  Abrir Foto em Nova Aba
 </a>
 <div id="errMsg" class="err">
  Não foi possível carregar a foto aqui.<br>
  Use o botão acima para abrir diretamente.
 </div>
</body>
</html>`);
   newTab.document.close();
  }

  toast.info('Foto aberta! Pressione e segure → Salvar em Fotos 📸', { duration: 8000 });
  return true;
 }

 // ============================================================
 // 🖥️ DESKTOP — Blob download automático
 // ============================================================
 try {
 // 1. Supabase Storage — signed URL para download
 const signedUrl = await getSupabaseSignedUrl(url, true);
 if (signedUrl) {
 const blob = await fetchBlob(signedUrl);
 if (blob) {
 blobDownload(blob, finalFilename);
 return true;
 }
 // Fallback: usar o <a> direto
 const link = document.createElement('a');
 link.href = signedUrl;
 link.download = finalFilename;
 link.style.display = 'none';
 document.body.appendChild(link);
 link.click();
 setTimeout(() => document.body.removeChild(link), 100);
 return true;
 }

 // 2. Google Drive
 if (url.includes('drive.google.com')) {
 const idMatch = url.match(/[-\w]{25,}/);
 if (idMatch) {
 const driveUrl = `https://drive.google.com/uc?export=download&id=${idMatch[0]}`;
 const iframe = document.createElement('iframe');
 iframe.style.display = 'none';
 iframe.src = driveUrl;
 document.body.appendChild(iframe);
 setTimeout(() => { if (document.body.contains(iframe)) document.body.removeChild(iframe); }, 15000);
 return true;
 }
 }

 // 3. Blob fetch universal
 const blob = await fetchBlob(url);
 if (blob && blob.size > 100) {
 blobDownload(blob, finalFilename);
 return true;
 }
 } catch (err) {
 console.warn('Desktop download error:', err);
 }

 // Último fallback
 window.open(url, '_blank');
 return true;
};
export const downloadAllAsZip = async (items: {url: string, filename: string}[], zipName: string) => {
  toast.loading(`Compactando ${items.length} arquivo(s). Por favor aguarde...`, { id: 'zip-download' });
  try {
    const zip = new JSZip();
    
    // Process in batches
    const BATCH_SIZE = 5;
    for (let i = 0; i < items.length; i += BATCH_SIZE) {
      const batch = items.slice(i, i + BATCH_SIZE);
      await Promise.all(batch.map(async (item) => {
        try {
          let resolvedUrl = item.url;
          const signedUrl = await getSupabaseSignedUrl(item.url, false);
          if (signedUrl) resolvedUrl = signedUrl;
          
          if (item.url.includes('drive.google.com')) {
            const idMatch = item.url.match(/[-\w]{25,}/);
            if (idMatch) resolvedUrl = `https://drive.google.com/uc?export=download&id=${idMatch[0]}`;
          }
          
          const blob = await fetchBlob(resolvedUrl);
          if (blob) {
            zip.file(item.filename, blob);
          }
        } catch (e) {
          console.error("Erro ao baixar item para zip:", item.filename, e);
        }
      }));
    }
    
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, zipName);
    toast.success("Download do ZIP conclu�do!", { id: "zip-download" });
  } catch (error) {
    console.error("Erro ao gerar zip:", error);
    toast.error("Erro ao gerar arquivo ZIP.", { id: "zip-download" });
  }
};
